import { ListFilter } from "lucide-react";
import { Dialog, DialogTrigger } from "react-aria-components";

import { Button } from "@/components/button";
import { Popover } from "@/components/popover";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";

import type { InsightsFilters } from "./insightsFilters";

import { SOCIAL_PLATFORM_LABELS } from "../socialPlatformLabels";
import { DATE_RANGE_OPTIONS } from "./insightsFilters";
import { POST_FORMAT_LABELS } from "./postFormatLabels";

const ALL = "all";

const FORMAT_OPTIONS = [
  { id: ALL, label: "All post types" },
  ...Object.entries(POST_FORMAT_LABELS).map(([id, label]) => ({ id, label })),
];

const PLATFORM_OPTIONS = [
  { id: ALL, label: "All platforms" },
  { id: "instagram", label: SOCIAL_PLATFORM_LABELS.instagram },
  { id: "facebook", label: SOCIAL_PLATFORM_LABELS.facebook },
];

export function InsightsFiltersPopover({
  filters,
  onChange,
}: {
  filters: InsightsFilters;
  onChange: (filters: InsightsFilters) => void;
}) {
  return (
    <DialogTrigger>
      <Button color="gray" variant="secondary">
        <ListFilter className="size-5" />
        Filters
      </Button>
      <Popover className="w-72" placement="bottom end">
        <Dialog
          aria-label="Insights filters"
          className="flex flex-col gap-lg p-lg outline-hidden"
        >
          <FilterSelect
            label="Date range"
            onChange={(id) => onChange({ ...filters, days: Number(id) })}
            options={DATE_RANGE_OPTIONS.map(({ days, label }) => ({
              id: String(days),
              label,
            }))}
            value={String(filters.days)}
          />
          <FilterSelect
            label="Post type"
            onChange={(id) =>
              onChange({
                ...filters,
                format:
                  id === ALL ? undefined : (id as InsightsFilters["format"]),
              })
            }
            options={FORMAT_OPTIONS}
            value={filters.format ?? ALL}
          />
          <FilterSelect
            label="Platform"
            onChange={(id) =>
              onChange({
                ...filters,
                platform:
                  id === ALL ? undefined : (id as InsightsFilters["platform"]),
              })
            }
            options={PLATFORM_OPTIONS}
            value={filters.platform ?? ALL}
          />
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function FilterSelect({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (id: string) => void;
  options: { id: string; label: string }[];
  value: string;
}) {
  return (
    <div className="flex flex-col gap-xs">
      <span className="text-sm font-medium text-text-secondary">{label}</span>
      <Select
        onSelectionChange={(key) => onChange(String(key))}
        selectedKey={value}
      >
        <SelectButton className="py-sm text-sm" />
        <SelectPopover>
          <SelectListBox items={options}>
            {(option) => (
              <SelectListItem id={option.id}>{option.label}</SelectListItem>
            )}
          </SelectListBox>
        </SelectPopover>
      </Select>
    </div>
  );
}
